import { useState, useMemo, useRef, useEffect, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconX, IconLoader2 } from '@tabler/icons-react';
import Highlighter from 'react-highlight-words';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useDocSearch, SearchResult } from '../hooks/useDocSearch';

/**
 * Props for the SearchBox component
 */
interface SearchBoxProps {
  /** Organization ID for navigation context */
  organizationId: string;
  /** Optional CSS class name */
  className?: string;
  /** Callback when a result is selected */
  onResultSelect?: () => void;
}

/**
 * SearchBox component
 *
 * @description
 * A search input with dropdown results for searching documentation.
 * Features include:
 * - Debounced search (300ms delay) using useDocSearch hook
 * - Dropdown results list with highlighting
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Click outside to close
 * - Navigate to selected documentation page
 * - Loading and error states with visual indicators
 * - Theme-aware styling
 * - Performance optimized with debouncing to reduce search calls
 *
 * Uses react-highlight-words to highlight matching search terms in results.
 * Search is debounced to prevent excessive searching on every keystroke.
 *
 * @example
 * ```tsx
 * function DocumentationHeader() {
 *   const { organization_id } = useParams();
 *
 *   return (
 *     <SearchBox
 *       organizationId={organization_id}
 *       onResultSelect={() => console.log('Result selected')}
 *     />
 *   );
 * }
 * ```
 */
export function SearchBox({
  organizationId: _organizationId,
  className,
  onResultSelect,
}: SearchBoxProps) {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce the search query to reduce search calls (300ms delay)
  const debouncedQuery = useDebounce(query, 300);

  // Track if search is pending (user is typing but debounce hasn't completed)
  const isSearchPending = query !== debouncedQuery && query.trim().length > 0;

  // Use the documentation search hook
  const { search, isLoading, error, isReady } = useDocSearch();

  // Get search results using debounced query
  const results = useMemo(() => {
    if (!debouncedQuery.trim() || !isReady) return [];
    return search(debouncedQuery).slice(0, 8); // Limit to 8 results
  }, [debouncedQuery, search, isReady]);

  /**
   * Handle clicking outside to close dropdown
   */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Navigate to a documentation page
   */
  const navigateToDoc = (path: string) => {
    // Remove .md extension for URL routing
    const cleanPath = path.replace(/\.md$/, '');
    // Use absolute path to ensure correct routing regardless of trailing slash
    navigate(`/documentation/${cleanPath}`, { replace: false });
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    onResultSelect?.();
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Escape') {
        setQuery('');
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          navigateToDoc(results[selectedIndex].path);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  /**
   * Handle input change
   */
  const handleInputChange = (value: string) => {
    setQuery(value);
    setSelectedIndex(-1);
    setIsOpen(value.trim().length > 0);
  };

  /**
   * Clear search
   */
  const handleClear = () => {
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  /**
   * Render a search result item
   */
  const renderResult = (result: SearchResult, index: number) => {
    const isSelected = index === selectedIndex;

    return (
      <button
        key={result.id}
        type="button"
        onClick={() => navigateToDoc(result.path)}
        onMouseEnter={() => setSelectedIndex(index)}
        className={cn(
          'w-full px-4 py-3 text-left transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-inset',
          isSelected
            ? 'bg-gray-100 dark:bg-gray-800'
            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
        )}
        style={{}}
        role="option"
        aria-selected={isSelected}
      >
        {/* Title with highlighting */}
        <div className="font-medium text-sm mb-1" style={{ color: currentTheme?.['primary-text'] }}>
          <Highlighter
            searchWords={result.terms}
            autoEscape
            textToHighlight={result.title}
            highlightClassName="bg-yellow-200 dark:bg-yellow-800"
          />
        </div>

        {/* Excerpt with highlighting */}
        <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-1">
          <Highlighter
            searchWords={result.terms}
            autoEscape
            textToHighlight={result.excerpt}
            highlightClassName="bg-yellow-200 dark:bg-yellow-800"
          />
        </div>

        {/* Category */}
        <div className="text-xs text-gray-500 dark:text-gray-500">{result.category}</div>
      </button>
    );
  };

  return (
    <div ref={searchBoxRef} className={cn('relative w-full', className)}>
      {/* Search Input */}
      <div className="relative">
        <IconSearch
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10"
          aria-hidden="true"
        />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search documentation..."
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim().length > 0 && setIsOpen(true)}
          className="pl-10 pr-10"
          aria-label="Search documentation"
          aria-autocomplete="list"
          aria-controls="search-results"
          aria-expanded={isOpen && results.length > 0}
          role="combobox"
        />
        {/* Show loading spinner while debouncing or searching */}
        {(isSearchPending || (isLoading && query.trim().length > 0)) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            <IconLoader2 size={18} className="animate-spin" aria-label="Searching..." />
          </div>
        )}
        {/* Show clear button when not loading */}
        {query && !isSearchPending && !isLoading && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Clear search"
            type="button"
          >
            <IconX size={18} />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && query.trim() && (
        <div
          id="search-results"
          className={cn(
            'absolute top-full left-0 right-0 mt-2 rounded-md border shadow-lg z-50',
            'max-h-96 overflow-y-auto',
            'bg-white dark:bg-gray-950',
          )}
          style={{
            borderColor: currentTheme?.['border-color'] || '#e5e7eb',
            backgroundColor: currentTheme?.background || '#ffffff',
          }}
          role="listbox"
        >
          {/* Loading State - Initial search index loading */}
          {isLoading && !isReady && (
            <div className="px-4 py-8 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                <IconLoader2 size={16} className="animate-spin" />
                <span>Loading search index...</span>
              </div>
            </div>
          )}

          {/* Searching State - Debounce in progress */}
          {isSearchPending && isReady && (
            <div className="px-4 py-8 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                <IconLoader2 size={16} className="animate-spin" />
                <span>Searching...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to load search. Please try again.
              </p>
            </div>
          )}

          {/* No Results */}
          {!isLoading &&
            !isSearchPending &&
            !error &&
            isReady &&
            results.length === 0 &&
            debouncedQuery.trim().length > 0 && (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No results found for &quot;{debouncedQuery}&quot;
                </p>
              </div>
            )}

          {/* Results List */}
          {!isLoading && !isSearchPending && !error && isReady && results.length > 0 && (
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {results.map((result, index) => renderResult(result, index))}
            </div>
          )}

          {/* Footer hint */}
          {!isLoading && !isSearchPending && !error && isReady && results.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Use{' '}
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700">
                  ↑
                </kbd>{' '}
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700">
                  ↓
                </kbd>{' '}
                to navigate,{' '}
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700">
                  Enter
                </kbd>{' '}
                to select,{' '}
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700">
                  Esc
                </kbd>{' '}
                to close
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
